import axios from 'axios';

// Get environment variables with proper fallbacks
const getEnvVar = (key, fallback) => {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
        return import.meta.env[key] || fallback;
    }
    return fallback;
};

const API_BASE_URL = getEnvVar('VITE_API_BASE_URL', 'http://localhost:8000');
const API_VERSION = getEnvVar('VITE_API_VERSION', 'v1');

// Create axios instance
const api = axios.create({
    baseURL: `${API_BASE_URL}/api/${API_VERSION}`,
    headers: {
        'Content-Type': 'application/json',
    },
    withCredentials: true, // Important for CORS and authentication
});

// Helper function to get CSRF token from cookie
const getCSRFToken = () => {
    const name = 'csrftoken';
    let cookieValue = null;
    if (document.cookie && document.cookie !== '') {
        const cookies = document.cookie.split(';');
        for (let i = 0; i < cookies.length; i++) {
            const cookie = cookies[i].trim();
            if (cookie.substring(0, name.length + 1) === (name + '=')) {
                cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
                break;
            }
        }
    }
    return cookieValue;
};

// Request interceptor to add authentication token and CSRF token
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('access_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }

        // Add CSRF token for non-GET requests
        if (config.method && !['get', 'head', 'options'].includes(config.method.toLowerCase())) {
            const csrfToken = getCSRFToken();
            if (csrfToken) {
                config.headers['X-CSRFToken'] = csrfToken;
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor to handle token refresh and email verification
api.interceptors.response.use(
    (response) => {
        return response;
    },
    async (error) => {
        const originalRequest = error.config;

        // Handle email verification required (403 with specific error)
        if (error.response?.status === 403 &&
            error.response?.data?.error === 'email_verification_required') {
            // Don't retry for email verification - let the component handle it
            return Promise.reject(error);
        }

        // Handle token refresh for 401 errors
        if (error.response?.status === 401 && !originalRequest._retry) {
            originalRequest._retry = true;

            try {
                const refreshToken = localStorage.getItem('refresh_token');
                if (refreshToken) {
                    const response = await axios.post(`${API_BASE_URL}/api/${API_VERSION}/users/auth/refresh/`, {
                        refresh: refreshToken
                    });

                    const { access, refresh } = response.data;
                    localStorage.setItem('access_token', access);

                    // Handle token rotation - store new refresh token if provided
                    if (refresh) {
                        localStorage.setItem('refresh_token', refresh);
                    }

                    // Retry original request with new token
                    originalRequest.headers.Authorization = `Bearer ${access}`;
                    return api(originalRequest);
                }
            } catch (refreshError) {
                // Refresh failed, redirect to login
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('user');
                window.location.href = '/login';
                return Promise.reject(refreshError);
            }
        }

        return Promise.reject(error);
    }
);

// API Service object with methods (new organized structure)
export const apiService = {
    // Authentication
    auth: {
        async login(credentials) {
            const response = await api.post('/users/auth/login/', credentials);
            const { access, refresh } = response.data;

            if (access && refresh) {
                localStorage.setItem('access_token', access);
                localStorage.setItem('refresh_token', refresh);
            }

            return response.data;
        },

        async register(userData) {
            const response = await api.post('/users/auth/register/', userData);
            return response.data;
        },

        async logout() {
            try {
                const refreshToken = localStorage.getItem('refresh_token');
                if (refreshToken) {
                    await api.post('/users/auth/logout/', { refresh_token: refreshToken });
                }
            } catch (error) {
                console.error('Logout API call failed:', error);
            } finally {
                localStorage.removeItem('access_token');
                localStorage.removeItem('refresh_token');
                localStorage.removeItem('user');
            }
        },

        async refreshToken() {
            const refreshToken = localStorage.getItem('refresh_token');
            if (!refreshToken) {
                throw new Error('No refresh token available');
            }

            const response = await api.post('/users/auth/refresh/', {
                refresh: refreshToken
            });

            const { access, refresh } = response.data;
            localStorage.setItem('access_token', access);

            // Handle token rotation
            if (refresh) {
                localStorage.setItem('refresh_token', refresh);
            }

            return access;
        },

        async getCurrentUser() {
            const response = await api.get('/users/profile/');
            return response.data;
        },

        isAuthenticated() {
            return !!localStorage.getItem('access_token');
        }
    },

    // Products
    products: {
        async getAll(params = {}) {
            const response = await api.get('/products/', { params });
            return response.data;
        },

        async getById(id) {
            const response = await api.get(`/products/${id}/`);
            return response.data;
        },

        async search(query, filters = {}) {
            const params = { search: query, ...filters };
            const response = await api.get('/products/search/', { params });
            return response.data;
        },

        async getCategories() {
            const response = await api.get('/products/categories/');
            return response.data;
        },

        async getRecommendations(params = {}) {
            const response = await api.get('/products/recommendations/', { params });
            return response.data;
        }
    },

    // Wishlists
    wishlists: {
        async getAll() {
            const response = await api.get('/wishlists/');
            return response.data;
        },

        async getById(id) {
            const response = await api.get(`/wishlists/${id}/`);
            return response.data;
        },

        async create(wishlistData) {
            const response = await api.post('/wishlists/', wishlistData);
            return response.data;
        },

        async update(id, wishlistData) {
            const response = await api.patch(`/wishlists/${id}/`, wishlistData);
            return response.data;
        },

        async delete(id) {
            await api.delete(`/wishlists/${id}/`);
        },

        async addProduct(wishlistId, productId) {
            const response = await api.post(`/wishlists/${wishlistId}/products/`, {
                product_id: productId
            });
            return response.data;
        },

        async removeProduct(wishlistId, productId) {
            await api.delete(`/wishlists/${wishlistId}/products/${productId}/`);
        }
    },

    // Occasions
    occasions: {
        async getAll(params = {}) {
            const response = await api.get('/occasions/', { params });
            return response.data;
        },

        async getById(id) {
            const response = await api.get(`/occasions/${id}/`);
            return response.data;
        },

        async create(occasionData) {
            const response = await api.post('/occasions/', occasionData);
            return response.data;
        },

        async update(id, occasionData) {
            const response = await api.patch(`/occasions/${id}/`, occasionData);
            return response.data;
        },

        async delete(id) {
            await api.delete(`/occasions/${id}/`);
        }
    },

    // Giveaways
    giveaways: {
        async getActive() {
            const response = await api.get('/giveaways/active/');
            return response.data;
        },

        async getById(id) {
            const response = await api.get(`/giveaways/${id}/`);
            return response.data;
        },

        async participate(id) {
            const response = await api.post(`/giveaways/${id}/participate/`);
            return response.data;
        },

        async getParticipations() {
            const response = await api.get('/giveaways/participations/');
            return response.data;
        }
    },

    // Recommendations
    recommendations: {
        async getForUser(params = {}) {
            const response = await api.get('/recommendations/', { params });
            return response.data;
        },

        async getByOccasion(occasionId) {
            const response = await api.get(`/recommendations/occasion/${occasionId}/`);
            return response.data;
        },

        async provideFeedback(recommendationId, feedback) {
            const response = await api.post(`/recommendations/${recommendationId}/feedback/`, feedback);
            return response.data;
        }
    },

    // Contacts
    contacts: {
        async getAll() {
            const response = await api.get('/contacts/');
            return response.data;
        },

        async create(contactData) {
            const response = await api.post('/contacts/', contactData);
            return response.data;
        },

        async update(id, contactData) {
            const response = await api.patch(`/contacts/${id}/`, contactData);
            return response.data;
        },

        async delete(id) {
            await api.delete(`/contacts/${id}/`);
        }
    },

    // Reminders
    reminders: {
        async getAll() {
            const response = await api.get('/reminders/', { params });
            return response.data;
        },

        async create(reminderData) {
            const response = await api.post('/reminders/', reminderData);
            return response.data;
        },

        async update(id, reminderData) {
            const response = await api.patch(`/reminders/${id}/`, reminderData);
            return response.data;
        },

        async delete(id) {
            await api.delete(`/reminders/${id}/`);
        }
    }
};

export default api; 